/**
 * Operators screen (SASADM-0077 / SASATH-0116). Lists platform operators
 * (`platform_admins`) and lets a SUPER_ADMIN grant / revoke over
 * `/api/saas-admin/operators`. Mirrors the Users list table + the
 * TenantInvitations grant/revoke dialog conventions (Dialog form, ConfirmDialog,
 * useSnackbar, useRevalidator post-mutation refresh).
 *
 * The loader role-gates BEFORE hitting the network so a non-super-admin never
 * triggers a backend 403; the screen renders the "Requires Super admin" Alert.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import { getClaims } from '@/auth/authStore';
import { useCurrentOperator } from '@/auth/AuthContext';
import type { OperatorDto, PlatformAdminRole } from '@/api/types';
import { PLATFORM_ADMIN_ROLES } from '@/api/types';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { PageHeader } from '@/components/PageHeader';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSnackbar } from '@/components/Snackbar';
import { useCopy } from '@/hooks/useCopy';

export interface OperatorsPanelLoaderData {
  operators: OperatorDto[];
  forbidden: boolean;
}

export async function operatorsListLoader(): Promise<OperatorsPanelLoaderData> {
  const claims = getClaims();
  const isSuper =
    claims?.platformAdmin === true && claims.platformAdminRole === 'SUPER_ADMIN';
  if (!isSuper) return { operators: [], forbidden: true };
  const operators = await apiAdmin.operators.list();
  return { operators, forbidden: false };
}

export function OperatorsPanel() {
  const { operators, forbidden } = useLoaderData() as OperatorsPanelLoaderData;
  const operator = useCurrentOperator();
  const { t } = useCopy();
  const snack = useSnackbar();
  const revalidator = useRevalidator();
  const [openGrant, setOpenGrant] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<OperatorDto | null>(null);
  const [revoking, setRevoking] = useState(false);

  const breadcrumbs = (
    <Breadcrumbs
      items={[
        { label: t('nav.home', 'Home'), to: '/' },
        { label: t('nav.reference', 'Reference') },
        { label: t('nav.operators', 'Operators') },
      ]}
    />
  );

  // Role-gate the whole screen. The loader already returns `forbidden` for a
  // non-super principal (no network call); guard on the live operator too so a
  // stale render never exposes a mutation.
  if (forbidden || !operator?.isSuperAdmin) {
    return (
      <Box>
        {breadcrumbs}
        <PageHeader title={t('nav.operators', 'Operators')} />
        <Alert severity="warning">
          {t('errors.superAdminRequired', 'Requires Super admin.')}
        </Alert>
      </Box>
    );
  }

  async function revoke(target: OperatorDto) {
    setRevoking(true);
    try {
      await apiAdmin.operators.revoke(target.userId);
      snack.success(t('operators.revoke.success', 'Operator access revoked.'));
      revalidator.revalidate();
    } catch (err) {
      if (err instanceof ApiError) {
        snack.error(t(`errors.codes.${err.code}`, err.code));
      } else {
        snack.error(t('errors.generic', 'Something went wrong. Please try again.'));
      }
    } finally {
      setRevoking(false);
      setRevokeTarget(null);
    }
  }

  return (
    <Box>
      {breadcrumbs}
      <PageHeader
        title={t('nav.operators', 'Operators')}
        actions={
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={() => setOpenGrant(true)}
          >
            {t('operators.grant.action', 'Grant operator')}
          </Button>
        }
      />
      {operators.length === 0 ? (
        <Typography
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            py: 4,
          }}
        >
          {t('operators.list.empty', 'No operators yet.')}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('operators.list.columns.name', 'Name')}</TableCell>
                <TableCell>{t('operators.list.columns.email', 'Email')}</TableCell>
                <TableCell>{t('operators.list.columns.role', 'Role')}</TableCell>
                <TableCell>{t('operators.list.columns.granted', 'Granted')}</TableCell>
                <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {operators.map((o) => (
                <TableRow key={o.userId}>
                  <TableCell>{o.name ?? '—'}</TableCell>
                  <TableCell>{o.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={t(
                        `operator.role.${o.role}`,
                        o.role === 'SUPER_ADMIN' ? 'Super admin' : 'Admin',
                      )}
                      size="small"
                      color={o.role === 'SUPER_ADMIN' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{new Date(o.grantedAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      color="error"
                      onClick={() => setRevokeTarget(o)}
                    >
                      {t('operators.actions.revoke', 'Revoke')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <GrantOperatorDialog
        open={openGrant}
        onClose={() => setOpenGrant(false)}
        onGranted={(email) => {
          setOpenGrant(false);
          revalidator.revalidate();
          snack.success(
            t('operators.grant.success', 'Operator granted to {{email}}.', { email }),
          );
        }}
      />
      <ConfirmDialog
        open={Boolean(revokeTarget)}
        title={t('operators.revoke.title', 'Revoke operator?')}
        body={t(
          'operators.revoke.body',
          'Remove {{email}} as a platform operator. They lose all control-plane access.',
          { email: revokeTarget?.email ?? '' },
        )}
        severity="error"
        confirmLabel={t('operators.actions.revoke', 'Revoke')}
        busy={revoking}
        onConfirm={() => {
          if (revokeTarget) void revoke(revokeTarget);
        }}
        onCancel={() => setRevokeTarget(null)}
      />
    </Box>
  );
}

interface GrantOperatorDialogProps {
  open: boolean;
  onClose: () => void;
  onGranted: (email: string) => void;
}

function GrantOperatorDialog({ open, onClose, onGranted }: GrantOperatorDialogProps) {
  const { t } = useCopy();
  const snack = useSnackbar();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<PlatformAdminRole>('SAAS_ADMIN');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [prevOpen, setPrevOpen] = useState(open);

  // Reset the form on the open transition (render-phase state adjustment — no
  // effect needed).
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setEmail('');
      setRole('SAAS_ADMIN');
      setEmailError(null);
      setPending(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    setPending(true);
    setEmailError(null);
    try {
      await apiAdmin.operators.grant({ email: trimmed, role });
      onGranted(trimmed);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'USER_NOT_FOUND') {
        setEmailError(
          t('errors.codes.USER_NOT_FOUND', 'No user with that email exists yet.'),
        );
      } else if (err instanceof ApiError) {
        snack.error(t(`errors.codes.${err.code}`, err.code));
      } else {
        snack.error(t('errors.generic', 'Something went wrong. Please try again.'));
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <Dialog open={open} onClose={pending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('operators.grant.title', 'Grant operator')}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={submit} id="grant-operator-form" noValidate>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              type="email"
              required
              label={t('operators.grant.email', 'User email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={Boolean(emailError)}
              helperText={emailError ?? undefined}
            />
            <TextField
              select
              fullWidth
              label={t('operators.grant.role', 'Role')}
              value={role}
              onChange={(e) => setRole(e.target.value as PlatformAdminRole)}
            >
              {PLATFORM_ADMIN_ROLES.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(`operator.role.${r}`, r === 'SUPER_ADMIN' ? 'Super admin' : 'Admin')}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={pending}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button
          form="grant-operator-form"
          type="submit"
          variant="contained"
          disabled={pending}
        >
          {t('operators.grant.submit', 'Grant')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
